<template>
  <!-- Top of the process page, and of its activities (US 9369 + 9371). Same layout as the system and third-party heroes. -->
  <header class="sx-hero" qid="process-hero">
    <span class="sx-hero__logo is-process"><font-awesome-icon icon="sitemap"/></span>

    <div class="sx-hero__main">
      <router-link :to="{ name: 'processes' }" class="sx-hero__back" qid="process-back">
        <font-awesome-icon icon="angle-left"/>{{ $t('process_hero.crumb') }}<template v-if="process.process_group"> · {{ process.process_group.name }}</template>
      </router-link>
      <h1 class="sx-hero__name" qid="process-hero-name">
        <router-link v-if="linkName" :to="{ name: 'processes.view', params: { slug: process.slug } }">{{ process.name }}</router-link>
        <template v-else>{{ process.name }}</template>
      </h1>
      <p v-if="process.description" class="sx-hero__text">{{ plain(process.description) | truncate(220) }}</p>
      <div class="sx-hero__tags">
        <span class="sx-hero__tag is-mint"><font-awesome-icon icon="cube"/>{{ $tc('process_hero.activities', activityCount, { count: activityCount }) }}</span>
        <span v-if="!process.partition && getClient.parent" class="sx-hero__tag"><font-awesome-icon icon="lock"/>{{ $t('systems_overview.from_workspace', { name: getClient.parent.name }) }}</span>
        <span v-for="tag in shownTags" :key="tag.id || tag.name_translated" class="sx-hero__tag" :title="tag.name_translated"><font-awesome-icon icon="tag"/>{{ tag.name_translated | truncate(30) }}</span>
        <span v-if="hiddenTags" class="sx-hero__tag">+{{ hiddenTags }}</span>
        <!-- An open "Update basic info" task of mine, as on the system page -->
        <span v-if="task" class="sx-hero__task" qid="process-hero-task">
          <UpdateSingleTaskModal :task="task" :editIcon="true" @reloadContent="taskClosed"></UpdateSingleTaskModal>
        </span>
      </div>
    </div>

    <div class="sx-hero__people" qid="process-owners-list">
      <small>{{ $t('process_hero.people') }}</small>
      <template v-if="people.length">
        <div class="sx-hero__avatars">
          <Avatar v-for="user in people.slice(0, 4)" :key="user.id" :object="user" size="sm" :noOwner="true"></Avatar>
        </div>
        <span class="sx-hero__who">
          {{ people[0].name }}
          <span v-if="people.length > 1" class="sx-hero__faint">{{ $t('system_hero.more_people', { count: people.length - 1 }) }}</span>
        </span>
      </template>
      <span v-else class="sx-hero__who is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_hero.nobody') }}</span>
    </div>

    <div class="sx-hero__done" qid="process-completeness">
      <svg class="sx-hero__ring" viewBox="0 0 76 76" aria-hidden="true">
        <circle cx="38" cy="38" r="32" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="7"/>
        <circle v-if="done"
                cx="38" cy="38" r="32" fill="none" stroke="#7FE0C4" stroke-width="7" stroke-linecap="round"
                transform="rotate(-90 38 38)"
                :stroke-dasharray="ringDash"/>
      </svg>
      <div class="sx-hero__done-text is-clamped" :title="missingText">
        <b>{{ $t('system_hero.filled', { done: done, total: checks.length }) }}</b>
        <span>{{ missingText }}</span>
      </div>
    </div>

    <!-- Always the process's, also while one of its activities is picked -->
    <div class="sx-hero__act">
      <div class="sx-hero__row">
        <!-- History opens from the hero instead of the tab bar, as on projects -->
        <button type="button"
                class="sx-hero__btn"
                :class="{ 'is-white': historyOn }"
                v-b-tooltip.hover
                :title="$t('processes.tab.history')"
                qid="process-open-history"
                @click="$emit('history')">
          <font-awesome-icon icon="history"/><span class="sr-only">{{ $t('processes.tab.history') }}</span>
        </button>
        <button v-if="canEdit"
                type="button"
                class="sx-hero__btn sx-hero__grow"
                qid="edit-process-button"
                @click="$emit('edit')">
          <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
        </button>
        <b-dropdown right
                    no-caret
                    variant="link"
                    toggle-class="sx-hero__btn"
                    menu-class="sx-menu"
                    qid="process-more">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span>
          </template>
          <b-dropdown-item v-if="canEdit" @click="$emit('edit')"><font-awesome-icon icon="pen"/>{{ $t('system_hero.menu.edit') }}</b-dropdown-item>
          <b-dropdown-item qid="process-print" @click="print"><font-awesome-icon icon="file-alt"/>{{ $t('system_hero.menu.export') }}</b-dropdown-item>
          <template v-if="canDelete">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item link-class="is-danger" qid="delete-p-button" @click="$emit('delete')"><font-awesome-icon icon="trash-alt"/>{{ $t('system_hero.menu.delete') }}</b-dropdown-item>
          </template>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import Avatar from '@/components/Avatar.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';

  export default {
    name: 'ProcessHero',
    components: {
      Avatar,
      UpdateSingleTaskModal
    },
    data() {
      return {
        task: null
      };
    },
    props: {
      process: { type: Object, required: true },
      // The row under the hero (processes/{process}/switcher): its activities and whether the legal basis is missing
      row: { type: Object, default: null },
      // On an activity, the name links back to the process
      linkName: { type: Boolean, default: false },
      // The process's history is open
      historyOn: { type: Boolean, default: false }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      editable() {
        return !this.getClient.parent || this.process.partition;
      },
      canEdit() {
        return this.isGranted([this.permissions.PROCESS_EDIT, this.permissions.PROCESS_EDIT_OWN], this.process) && this.editable;
      },
      canDelete() {
        return this.isGranted([this.permissions.PROCESS_DELETE, this.permissions.PROCESS_DELETE_OWN], this.process) && this.editable;
      },
      activityCount() {
        return this.row ? this.row.children.filter(child => !child.shared).length : 0;
      },
      tags() {
        return this.process.tags_translated || [];
      },
      shownTags() {
        return this.tags.slice(0, 3);
      },
      hiddenTags() {
        return Math.max(this.tags.length - 3, 0);
      },
      people() {
        let users = this.process.users || [];
        let owners = users.filter(user => user.pivot && user.pivot.owner);
        return owners.concat(users.filter(user => owners.indexOf(user) === -1));
      },
      checks() {
        return [
          { key: 'description', ok: !!this.plain(this.process.description) },
          { key: 'owner', ok: this.people.length > 0 },
          { key: 'group', ok: !!this.process.process_group },
          { key: 'legal_basis', ok: !(this.row && this.row.parent && this.row.parent.warn) },
          { key: 'activity', ok: this.activityCount > 0 }
        ];
      },
      done() {
        return this.checks.filter(check => check.ok).length;
      },
      ringDash() {
        let length = 2 * Math.PI * 32;
        return (length * this.done / this.checks.length) + ' ' + length;
      },
      missingText() {
        let missing = this.checks.filter(check => !check.ok);
        if (!missing.length) {
          return this.$t('system_hero.all_filled');
        }
        let names = missing.map(check => this.$t('process_hero.missing.' + check.key).toLowerCase());
        return this.$t('system_hero.missing', { list: names.join(', ') });
      }
    },
    watch: {
      'process.id': function() {
        this.loadTask();
      }
    },
    created() {
      this.loadTask();
    },
    methods: {
      // Also on its activities' pages, so the hero loads it rather than the page
      loadTask() {
        this.task = null;
        if (!this.process.id || !this.getClient.projectModule) {
          return;
        }
        piincHttp.get('projects/me/tasks', { params: {
          per_page: 1,
          status: 'pending',
          model_type: this.process.who_am_i,
          model_id: this.process.id,
          code: 'update_basic_info',
          client: this.getClient.slug
        } }).then(response => {
          this.task = response.data.items.length ? response.data.items[0] : null;
        }, () => {});
      },
      taskClosed() {
        this.loadTask();
        this.$emit('updated');
      },
      print() {
        window.print();
      },
      // Descriptions may hold formatting (US 9390)
      plain(text) {
        let element = document.createElement('div');
        element.innerHTML = text || '';
        return (element.textContent || '').trim();
      }
    }
  };
</script>
